import type { ReactNode } from "react";

/**
 * One column/data definition shared by `DataTable` (desktop/tablet) and
 * `RecordList` (mobile) — design.md §6 "Tables, forms, and icons": "responsive
 * record alternatives generated from the same data/column definition." Never
 * duplicate a column list between the two renderings.
 */
export interface DataTableColumn<T> {
  readonly key: string;
  readonly header: string;
  readonly render: (row: T) => ReactNode;
  // Opt-in per-column alignment (e.g. Plantillas' "Versión activa",
  // Segmentos' "Miembros actuales (resueltos)" — `Panel Precision
  // App.dc.html` right-aligns every numeric column). Omit for the default
  // left alignment every other column already uses.
  readonly align?: "right" | "center";
  // Opt-in replacement for the plain-text `header` (feat/campaign-list-sort-show-column):
  // a column that needs an interactive header (e.g. a sortable "Campaña" column's
  // clickable button + direction chevron) supplies this instead of relying on
  // `header` being rendered as a bare text node. `header` itself stays a plain
  // string always (used for anything that needs the column's plain-text label,
  // e.g. future aria-describedby wiring) — this primitive stays a dumb rendering
  // shape; the sort STATE/LOGIC lives entirely in the feature screen, never here.
  readonly headerRender?: () => ReactNode;
}

/**
 * Generic semantic table (design.md §6: "Tables retain semantic <table>,
 * <caption>, scoped headers... Do not turn semantic table elements into CSS
 * grid."). Feature screens supply the column/row data; this primitive owns
 * only the markup shape (design.md §7 "primitives own semantics and states").
 */
export function DataTable<T>({
  ariaLabel,
  caption,
  columns,
  rows,
  rowKey,
  isRowBlocked,
}: {
  ariaLabel: string;
  caption: string;
  columns: readonly DataTableColumn<T>[];
  rows: readonly T[];
  rowKey: (row: T) => string;
  // 07-directrices-movil.md §3: a row this app cannot act on yet (e.g.
  // TelemetryEvidenceMatrix's "Conteos no disponibles") is marked
  // `aria-disabled`, never silently indistinguishable from an available row.
  // Optional — every other caller (the vast majority) omits it.
  isRowBlocked?: (row: T) => boolean;
}) {
  return (
    // UX audit 2026-09-15, finding #22: an unwrapped wide table dragged the
    // WHOLE page (sidebar included) into horizontal scroll instead of
    // scrolling within its own box — every screen using this shared
    // primitive (Campañas, Contactos, Segmentos, Plantillas) inherits the
    // fix from here, same "one shared component" discipline as `Field.tsx`.
    <div className="v2-u-table-scroll">
      <table aria-label={ariaLabel} className="v2-data-table">
        <caption>{caption}</caption>
        <thead>
          <tr>
            {columns.map((column) => (
              <th scope="col" key={column.key} className={column.align ? `v2-data-table__cell--${column.align}` : undefined}>
                {column.headerRender ? column.headerRender() : column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={rowKey(row)} aria-disabled={isRowBlocked?.(row) ? "true" : undefined}>
              {columns.map((column) => (
                <td key={column.key} className={column.align ? `v2-data-table__cell--${column.align}` : undefined}>
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
